import { useEffect, useRef, useCallback } from 'react';

interface RazorpayButtonProps {
  paymentButtonId?: string;
  subscriptionButtonId?: string;
  buttonType?: 'payment' | 'subscription';
  buttonTheme?: string;
  className?: string;
}

const RazorpayButton = ({ 
  paymentButtonId, 
  subscriptionButtonId,
  buttonType = 'payment',
  buttonTheme = 'brand-color',
  className = "" 
}: RazorpayButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  const loadRazorpayScript = useCallback(() => {
    if (!containerRef.current || scriptLoadedRef.current) return;

    containerRef.current.innerHTML = '';

    const form = document.createElement('form');
    const script = document.createElement('script');

    if (buttonType === 'subscription') {
      if (!subscriptionButtonId) {
        console.error('subscriptionButtonId is required for subscription button type');
        return;
      }
      script.src = 'https://cdn.razorpay.com/static/widget/subscription-button.js';
      script.setAttribute('data-subscription_button_id', subscriptionButtonId);
      script.setAttribute('data-button_theme', buttonTheme);
    } else {
      if (!paymentButtonId) {
        console.error('paymentButtonId is required for payment button type');
        return;
      }
      script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
      script.setAttribute('data-payment_button_id', paymentButtonId);
    }

    script.async = true;
    script.defer = true;

    script.onload = () => {
      scriptLoadedRef.current = true;
      
      // Add click listener and mutation observer to handle modal visibility
      setTimeout(() => {
        if (containerRef.current) {
          const button = containerRef.current.querySelector('button');
          if (button) {
            button.addEventListener('click', () => {
              // Immediately scroll to top before modal opens
              window.scrollTo({ top: 0, behavior: 'instant' });
              document.documentElement.scrollTop = 0;
              document.body.scrollTop = 0;
              
              // Watch for Razorpay modal/iframe to appear in DOM
              const observer = new MutationObserver(() => {
                // Look for any Razorpay modal elements
                const razorpayModal = document.querySelector('iframe[name*="razorpay"]') ||
                                     document.querySelector('[class*="razorpay"]') ||
                                     document.querySelector('#payment-form');
                
                if (razorpayModal) {
                  // Force scroll to top multiple times to ensure visibility
                  window.scrollTo({ top: 0, behavior: 'instant' });
                  document.documentElement.scrollTop = 0;
                  document.body.scrollTop = 0;
                  
                  // Try to adjust modal position if possible
                  const modalParent = razorpayModal.parentElement;
                  if (modalParent && modalParent.style) {
                    modalParent.style.top = '0';
                    modalParent.style.position = 'fixed';
                  }
                  
                  // Continue checking for a bit in case modal repositions
                  let checkCount = 0;
                  const intervalId = setInterval(() => {
                    window.scrollTo({ top: 0, behavior: 'instant' });
                    document.documentElement.scrollTop = 0;
                    document.body.scrollTop = 0;
                    checkCount++;
                    if (checkCount > 10) {
                      clearInterval(intervalId);
                      observer.disconnect();
                    }
                  }, 100);
                }
              });
              
              // Start observing the document body for changes
              observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true
              });
              
              // Also scroll periodically for the first 2 seconds
              let scrollCount = 0;
              const scrollInterval = setInterval(() => {
                window.scrollTo({ top: 0, behavior: 'instant' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                scrollCount++;
                if (scrollCount > 20) {
                  clearInterval(scrollInterval);
                }
              }, 100);
              
              // Disconnect observer after 5 seconds to prevent memory leaks
              setTimeout(() => {
                observer.disconnect();
              }, 5000);
            });
          }
        }
      }, 500);
    };

    form.appendChild(script);
    containerRef.current.appendChild(form);
  }, [paymentButtonId, subscriptionButtonId, buttonType, buttonTheme]);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(loadRazorpayScript);
    } else {
      setTimeout(loadRazorpayScript, 100);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      scriptLoadedRef.current = false;
    };
  }, [loadRazorpayScript]);

  return (
    <div 
      ref={containerRef} 
      className={`text-center min-h-[40px] ${className}`}
      style={{ minHeight: '40px' }}
    />
  );
};

export default RazorpayButton;


